import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { VehicleCheckInDetailsDto, VehicleDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import VehicleCheckInEditDialog from './VehicleCheckInEditDialog'
import { formatDate, formatDecimal, notNullOrUndefined } from 'utils'

interface VehicleCheckInDetailsProps {
  vehicle: VehicleDto
}

export default function VehicleCheckInDetailsCard({ vehicle }: VehicleCheckInDetailsProps) {
  const [isSavingVehicleCheckInDetails, setIsSavingVehicleCheckInDetails] = useState<boolean>(false)
  const [isGettingVehicleCheckInDetails, setIsGettingVehicleCheckInDetails] = useState<boolean>(false)
  const [vehicleCheckInEditDialogOpen, setVehicleCheckInEditDialogOpen] = useState<boolean>(false)
  const [vehicleCheckInDetails, setVehicleCheckInDetails] = useState<VehicleCheckInDetailsDto>(new VehicleCheckInDetailsDto())

  const { permissions } = useAuth()

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingVehicleCheckInDetails(true)
      controllers
        .getVehicleCheckInDetails(vehicle.vehicleGuid!)
        .then(response => {
          setVehicleCheckInDetails(response.value)
        })
        .finally(() => {
          setIsGettingVehicleCheckInDetails(false)
        })
    }
  }, [vehicle])

  return (
    <>
      <VehicleCheckInEditDialog
        disabled={Boolean(vehicle.vehicleGuid && vehicle.isLocked) || vehicle.isCheckedIn === true}
        initialValues={vehicleCheckInDetails}
        isLoading={isSavingVehicleCheckInDetails}
        onClose={() => {
          setVehicleCheckInEditDialogOpen(false)
        }}
        onSave={values => {
          setIsSavingVehicleCheckInDetails(true)
          controllers
            .updateVehicleCheckInDetails(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Vehicle Check-In Vehicle Details Saved Successfully!', { variant: 'success' })
              setVehicleCheckInDetails(response.value)
              setVehicleCheckInEditDialogOpen(false)
            })
            .finally(() => {
              setIsSavingVehicleCheckInDetails(false)
            })
        }}
        open={vehicleCheckInEditDialogOpen}
      />
      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Vehicle Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    if (permissions.VEHICLE_CHECK_IN_EDIT) {
                      setVehicleCheckInEditDialogOpen(true)
                    }
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingVehicleCheckInDetails && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingVehicleCheckInDetails && (
              <>
                <Grid item xs={6}>
                  <LabelField label='Certificate Number' value={vehicleCheckInDetails.vehicleCheckInBondNumber} />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Vehicle Check-In Date'
                    value={vehicleCheckInDetails.vehicleCheckInDateTime ? formatDate(vehicleCheckInDetails.vehicleCheckInDateTime) : ''}
                  />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Vehicle Check-In Odometer Reading in KM'
                    value={
                      notNullOrUndefined(vehicleCheckInDetails.vehicleCheckInOdometerReadingKm)
                        ? vehicleCheckInDetails.vehicleCheckInOdometerReadingKm + ' KM'
                        : undefined
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Vehicle Check-In Odometer Reading in MI'
                    value={
                      notNullOrUndefined(vehicleCheckInDetails.vehicleCheckInOdometerReadingMi)
                        ? vehicleCheckInDetails.vehicleCheckInOdometerReadingMi + ' MI'
                        : undefined
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Convert Cluster'
                    value={vehicleCheckInDetails.convertCluster !== undefined ? (vehicleCheckInDetails.convertCluster ? 'Yes' : 'No') : ''}
                  />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Program Cluster'
                    value={
                      vehicleCheckInDetails.vehicleCheckInAdditionalProgramming !== undefined
                        ? vehicleCheckInDetails.vehicleCheckInAdditionalProgramming
                          ? 'Yes'
                          : 'No'
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Manufactured Date'
                    value={`${vehicleCheckInDetails.vehicleCheckInMFGBuildDateMonth}/${vehicleCheckInDetails.vehicleCheckInMFGBuildDateYear}`}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Weight
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='GVWR' value={vehicleCheckInDetails.vehicleCheckInWeightGVWR} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField
                    label='GVWR Unit Of Measurement'
                    value={vehicleCheckInDetails.vehicleCheckInWeightGVWRUnitOfMeasurement}
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='GVWR (Kg)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInWeightGVWRKg)} />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='GVWR (Lbs)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInWeightGVWRLbs)} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Curb Weight' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInCurbWeight)} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='GAWR FR' value={vehicleCheckInDetails.vehicleCheckInWeightGAWRFR} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField
                    label='GAWR FR Unit Of Measurement'
                    value={vehicleCheckInDetails.vehicleCheckInWeightGAWRFRUnitOfMeasurement}
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='GAWR FR (Kg)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInWeightGAWRFRKg)} />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='GAWR FR (Lbs)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInWeightGAWRFRLbs)} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='GAWR RR' value={vehicleCheckInDetails.vehicleCheckInWeightGAWRRR} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField
                    label='GAWR RR Unit Of Measurement'
                    value={vehicleCheckInDetails.vehicleCheckInWeightGAWRRRUnitOfMeasurement}
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='GAWR RR (Kg)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInWeightGAWRRRKg)} />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='GAWR RR (Lbs)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInWeightGAWRRRLbs)} />
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='body1'>
                    Tires
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='Front Tires' value={vehicleCheckInDetails.vehicleCheckInFrontTires} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='Front Rims' value={vehicleCheckInDetails.vehicleCheckInFrontRims} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='Front Tires Pressure' value={vehicleCheckInDetails.vehicleCheckInFrontTiresPressure} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField
                    label='Front Tires Pressure Unit Of Measurement'
                    value={vehicleCheckInDetails.vehicleCheckInFrontTiresPressureUnitOfMeasurement}
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='Front Tires (kPa)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInFrontTiresPressurekPa)} />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='Front Tires (PSI)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInFrontTiresPressurePSI)} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='Rear Tires' value={vehicleCheckInDetails.vehicleCheckInRearTires} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='Rear Rims' value={vehicleCheckInDetails.vehicleCheckInRearRims} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField label='Rear Tires Pressure' value={vehicleCheckInDetails.vehicleCheckInRearTiresPressure} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelField
                    label='Rear Tires Pressure Unit Of Measurement'
                    value={vehicleCheckInDetails.vehicleCheckInRearTiresPressureUnitOfMeasurement}
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='Rear Tires (kPa)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInRearTiresPressurekPa)} />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LabelField label='Rear Tires (PSI)' value={formatDecimal(vehicleCheckInDetails.vehicleCheckInRearTiresPressurePSI)} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Number of Rear Tires' value={vehicleCheckInDetails.vehicleCheckInNumberOfRearTires} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Rear Tire Config' value={vehicleCheckInDetails.vehicleCheckInRearTireConfig} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**EDIT Button:**

Clicking the EDIT button opens the Edit Vehicle Details dialog.

**Certificate Number:**

Certificate Number is the next available Bond # which is assigned to this vehicle at the time the Vehicle Check-In is completed. This Bond # is different from the Bond # on the vehicle’s Bond document which is part of the Inbound Trip process. The Certificate Number (aka Service Insurance Policy #) is used on the vehicle's Statement of Conformity. 

**Vehicle Check-In Odometer Reading in KM:**

Displays the vehicle's odometer reading in kilometers, as entered in the Edit Vehicle Details dialog.

If the odometer reading was originally entered in miles, this will display the converted value.

**Vehicle Check-In Odometer Reading in MI:**

Displays the vehicle's Odometer Reading in Miles as entered in the Vehicle Details Edit dialog.

If the odometer reading was originally entered in kilometers, this will display the converted value.

**Convert Cluster:**

Displays "Yes" or "No" to indicate if the cluster needs to be converted, as selected in the Edit Vehicle Details dialog.

If the odometer reading is entered as kilometers, this value will default to "Yes."

**Program Cluster:**

Displays "Yes" or "No" to indicate if the vehicle's cluster needs to be programmed.

**Manufactured Date:**

Displays the vehicle's manufactured date.

By default, it displays the manufacture date entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**GVWR:**

Displays the vehicle's Gross Vehicle Weight Rating (GVWR).

By default, it displays the GVWR entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**GAWR FR:**

Displays the vehicle's Gross Axle Weight Rating (Front).

By default, it displays the GAWR FR entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**GAWR RR:**

Displays the vehicle's Gross Axle Weight Rating (Rear).

By default, it displays the GAWR RR entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**Front Tires:**

Displays the details of the vehicle's front tires. 

By default, it will display the front tire information entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**Front Rims:**

Displays the details of the vehicle's front rims. 

By default, it will display the front rim information entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**Front Tires PSI:**

Displays the PSI (tire pressure) information for the vehicle's front tires. 

By default, it will display the PSI entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**Rear Tires:**

Displays the Rear Tires Make Information Entered for the Vehicle. 

By Default it will display the Rear Tires Make Information entered when the Vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**Rear Rims:**

Displays the details of the vehicle's rear rims. 

By default, it will display the rear rim information entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**Rear Tires PSI:**

Displays the PSI (tire pressure) information for the vehicle's rear tires. 

By default, it will display the PSI entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.

**Number or Rear Tires:**

Displays the number of rear tires specified for the vehicle. 

By default, it will display the number entered when the vehicle was added to the system.

This value can be updated in the Edit Vehicle Details dialog.`}
              </GreenText>

              {/* <ExplanationAccordion variant='outlined'>
                This card shows information entered by users during the vehicle check-in process.
              </ExplanationAccordion> */}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
